import React, { FC, Children, ReactNode } from "react";

import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import { Box, BoxProps } from "@UI/Components/Box/Box";
import { Button } from "@UI/Components/Button/Button";
import { Text } from "@UI/Components/Text/Text";
import { AlertBox } from "@UI/Components/AlertBox/AlertBox";
import { HStack, Stack } from "@UI/Components/Stack/Stack";
import { Heading } from "@UI/Components/Heading/Heading";
import messages from "./messages";
import { PaymentMethodIcons } from "../PaymentMethodIcons/PaymentMethodIcons";
import { PaymentMethodType } from "@modules/PaymentProcessing/types";
import { Flex } from "@UI/Components/Flex/Flex";
import { BackLink } from "@Shared/Components/BackLink/BackLink";

interface ErrorProps {
  onClick: () => void;
}

interface HeaderProps {
  onChangeClick: () => void;
  children: ReactNode;
  paymentMethod: PaymentMethodType;
  isAllowedPaymentMethodSingle?: boolean;
}

const Body: FC<BoxProps> = (props) => <Box {...props} />;
const Footer: FC<BoxProps> = (props) => <Box {...props} />;

const Error: FC<ErrorProps> = ({ onClick }) => {
  return (
    <AlertBox>
      <Box ml={3}>
        <Text as="span" color="gray.700">
          <FormattedMessage {...messages.tryLaterOrAnotherCard} />
        </Text>
        <Box>
          <BackLink noArrow onClick={onClick} />
        </Box>
      </Box>
    </AlertBox>
  );
};

const Header: FC<HeaderProps> = ({
  children,
  onChangeClick,
  paymentMethod,
  isAllowedPaymentMethodSingle,
}) => {
  return (
    <Flex
      flexDirection={["column", "column", "row"]}
      justifyContent="space-between"
      alignItems={["center", "center", "flex-start"]}
    >
      <Flex
        flexDirection={["row", "row", "column"]}
        alignItems={["center", "center", "start"]}
        mb={2.5}
      >
        <Heading
          fontSize="lg"
          fontFamily="Inter"
          mr={[2.5, 2.5, 0]}
          mb={[0, 0, 2.5]}
        >
          <HStack spacing={2} justifyContent="center" alignItems="center">
            <Flex>{children}</Flex>
            <PaymentMethodIcons paymentMethod={paymentMethod} />
          </HStack>
        </Heading>
      </Flex>
      {!isAllowedPaymentMethodSingle && (
        <Button
          variant="link"
          colorScheme="pink"
          paddingX="0"
          onClick={onChangeClick}
        >
          <Text fontSize="sm">
            <FormattedMessage {...messages.changePaymentMethod} />
          </Text>
        </Button>
      )}
    </Flex>
  );
};

interface Props {
  children: ReactNode;
}

export type IPaymentMethodForm = FC<Props> & {
  Error: FC<ErrorProps>;
  Header: FC<HeaderProps>;
  Body: FC<BoxProps>;
  Footer: FC<BoxProps>;
};

export const PaymentMethodForm = ({ children }) => {
  const slots = {
    error: null,
    header: null,
    body: null,
    footer: null,
  };

  Children.forEach(children, (child) => {
    if (child?.type === Error) {
      slots.error = child;
    } else if (child?.type === Header) {
      slots.header = child;
    } else if (child?.type === Body) {
      slots.body = child;
    } else if (child?.type === Footer) {
      slots.footer = child;
    }
  });

  return (
    <Stack spacing={5}>
      {slots.error}
      {slots.header}
      {slots.body}
      {slots.footer}
    </Stack>
  );
};

PaymentMethodForm.Error = Error;
PaymentMethodForm.Header = Header;
PaymentMethodForm.Body = Body;
PaymentMethodForm.Footer = Footer;
